import CloseIcon from '@mui/icons-material/Close'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  Drawer,
  IconButton,
  Portal,
  Typography,
  Tabs,
  Tab,
  Grid2,
  Chip,
  Fade,
  Backdrop,
} from '@mui/material'
import { useEffect, useState, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { formatPhoneNumber } from 'src/consumer/helpers/utilFunctions'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithParams } from 'src/mentor/services'
import { isTokenInvalidOrAbsent } from 'src/views/pages/login/ProtectedRoute'

// Video Thumbnail Card Component
function VideoThumbnailCard({ video, onClick }) {
  const thumbnailUrl = `https://img.youtube.com/vi/${video?.contentId}/maxresdefault.jpg`
  
  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        border: '1px solid rgba(67, 56, 202, 0.15)',
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        backgroundColor: 'white',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(67, 56, 202, 0.2)',
          borderColor: 'rgba(67, 56, 202, 0.3)',
        },
      }}
    >
      {/* Thumbnail */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingTop: '56.25%', // 16:9 aspect ratio
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={thumbnailUrl}
          alt={video?.name || 'Video thumbnail'}
          onError={(e) => {
            // Fallback to a lower quality thumbnail if maxresdefault fails
            e.target.src = `https://img.youtube.com/vi/${video?.contentId}/hqdefault.jpg`
          }}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)',
            },
          }}
        />
        {/* Play Button Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 64,
            height: 64,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translate(-50%, -50%) scale(1.1)',
              backgroundColor: 'white',
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
            },
          }}
        >
          <PlayCircleOutlineIcon
            sx={{
              fontSize: 40,
              color: 'rgba(67, 56, 202, 0.9)',
              marginLeft: '4px', // Slight offset for visual centering
            }}
          />
        </Box>
        {/* Duration Badge (if available) */}
        {video?.duration && (
          <Chip
            label={video.duration}
            size="small"
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              color: 'white',
              fontSize: '11px',
              fontWeight: 600,
              height: 20,
              '& .MuiChip-label': {
                padding: '0 6px',
              },
            }}
          />
        )}
      </Box>

      {/* Video Info */}
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: 'rgba(15, 23, 42, 0.95)',
            fontSize: '15px',
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minHeight: '42px',
          }}
        >
          {video?.name || 'Untitled Video'}
        </Typography>
        {video?.description && (
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              color: 'rgba(100, 116, 139, 0.7)',
              fontSize: '13px',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {video.description}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

export default function YoutubeHelper({
  fetchUrl = '/api/secure/teacher/doc-side-content',
  buttonTopOffset = 0,
  flowId,
  contextId,
}) {
  const location = useLocation()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [playlistLinks, setPlaylistLinks] = useState([])
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [supportNumber, setSupportNumber] = useState(null)
  const [supportWhatsappLink, setSupportWhatsappLink] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  const [bulletinContent, setBulletinContent] = useState([]) // Will be populated via API later

  // Check if we're in CA Portal
  const isCAPortal = location.pathname.startsWith('/ca-home') || location.pathname.startsWith('/kiosk')

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true)

  //sample object for playlistLinks
  // {
  //   "name": "Help Videos",
  //   "type": "playlist",
  //   "link": "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
  // }

  useEffect(() => {
    // Check token validity before making API call
    const token = localStorage.getItem('token')
    if (isTokenInvalidOrAbsent(token)) {
      // Don't make API call if token is invalid
      return
    }

    let isCancelled = false

    async function fetchPlaylists() {
      // Double-check token before making the request
      const currentToken = localStorage.getItem('token')
      if (isTokenInvalidOrAbsent(currentToken) || isCancelled) {
        return
      }

      try {
        const resp = await getMPDataWithParams(fetchUrl, {
          flowId,
          'context-id': contextId,
        })
        
        // Check if component is still mounted and not cancelled
        if (isCancelled || !isMountedRef.current) {
          return
        }

        // Check token again after response
        const tokenAfterResponse = localStorage.getItem('token')
        if (isTokenInvalidOrAbsent(tokenAfterResponse)) {
          return
        }

        if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
          const data = getDataMapFromCommonResponse(resp)
          let ytLinks = data?.['playable_external_content'] || []
          setPlaylistLinks(ytLinks)
          setSupportNumber(data?.['humanSupportNumber'])
          setSupportWhatsappLink(data?.['supportWhatsappLink'])
          // if (links.length > 0) {
          // setSelectedPlaylist(links[0])
          // }
        } else {
          console.error('Failed to load playlists:', resp)
        }
      } catch (err) {
        // Only log error if component is still mounted and not cancelled
        if (!isCancelled && isMountedRef.current) {
          // Don't log 401 errors as they're expected after logout
          if (err?.response?.status !== 401) {
            console.error('Failed to load playlists:', err)
          }
        }
      }
    }

    fetchPlaylists()

    // Cleanup function to cancel the request
    return () => {
      isCancelled = true
    }
  }, [fetchUrl, flowId, contextId])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Fetch bulletin content when API is ready
  // useEffect(() => {
  //   async function fetchBulletinContent() {
  //     try {
  //       const resp = await getMPDataWithParams('/api/secure/teacher/doc-side-bulletin', {
  //         flowId,
  //         'context-id': contextId,
  //       })
  //       if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
  //         const data = getDataMapFromCommonResponse(resp)
  //         setBulletinContent(data?.bulletins || [])
  //       }
  //     } catch (err) {
  //       console.error('Failed to load bulletin content:', err)
  //     }
  //   }
  //   fetchBulletinContent()
  // }, [fetchUrl, flowId, contextId])

  return (
    <>
      {/* Floating Help Button - Hidden when drawer or modal is open, and behind other drawers */}
      {playlistLinks?.length > 0 && !drawerOpen && !modalOpen && (
        <Portal>
          <Button
            onClick={() => setDrawerOpen((prev) => !prev)}
            size="large"
            className="hide-on-print-only"
            sx={{
              position: 'fixed',
              // For CA Portal: position in bottom half (25vh from bottom)
              // For other portals: position in top half (50vh + 120px from bottom)
              bottom: isCAPortal
                ? `calc(25vh - ${buttonTopOffset}px)`
                : `calc(50vh + 120px - ${buttonTopOffset}px)`,
              right: 0,
              bgcolor: 'emrPrimary.main',
              color: 'white',
              zIndex: 1100, // Lower than Material-UI Drawer default (1200) so it's behind drawers
              transition: 'all 225ms cubic-bezier(0, 0, 0.2, 1)',
              '&:hover': { bgcolor: 'primary.dark' },
              writingMode: 'vertical-lr',
              textOrientation: 'mixed',
              transform: 'rotate(180deg) !important',
              padding: '16px 0px',
              borderRadius: '0px',
              minWidth: '0px !important',
            }}
            disableRipple
          >
            DocSide
          </Button>
        </Portal>
      )}

          {/* Drawer with Tabs */}
          {playlistLinks?.length > 0 && (
            <Drawer
              anchor="right"
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              sx={{
                '& .MuiDrawer-paper': {
                  width: { xs: '100%', sm: 480, md: 640 },
                  boxSizing: 'border-box',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.98) 100%)',
                },
              }}
            >
            {/* Header */}
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1000,
                background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.95) 0%, rgba(79, 70, 229, 0.95) 100%)',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 4px 16px rgba(67, 56, 202, 0.2)',
                borderBottom: '1px solid rgba(67, 56, 202, 0.2)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '20px 24px',
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    color: 'white',
                    fontWeight: 700,
                    fontSize: { xs: '20px', sm: '24px' },
                    letterSpacing: '0.5px',
                  }}
                >
                  DocSide
                </Typography>
                <IconButton
                  onClick={() => setDrawerOpen(false)}
                  sx={{
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Support Card */}
              {!!supportNumber && (
                <Box sx={{ px: 3, pb: 2 }}>
                  <Card
                    elevation={0}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: 2,
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ mb: 1, fontSize: '0.875em', color: 'rgba(255, 255, 255, 0.9)' }}
                          >
                            थेरेपी बुक करने के लिए कृपया कॉल करें
                          </Typography>
                          <Typography
                            component="a"
                            href={`tel:${supportNumber}`}
                            sx={{
                              color: 'white',
                              textDecoration: 'none',
                              fontWeight: 600,
                              fontSize: '1.25em',
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {supportNumber &&
                              `${formatPhoneNumber(supportNumber)?.formatType2Number}`}
                          </Typography>
                        </Box>
                        <IconButton
                          component="a"
                          href={`${supportWhatsappLink}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: '#25D366',
                            backgroundColor: 'rgba(37, 211, 102, 0.15)',
                            '&:hover': {
                              backgroundColor: 'rgba(37, 211, 102, 0.25)',
                            },
                          }}
                        >
                          <WhatsAppIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* Tabs */}
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'white',
                    height: 3,
                  },
                }}
              >
                <Tab
                  label="Bulletin Board"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '15px',
                    '&.Mui-selected': {
                      color: 'white',
                    },
                  }}
                />
                <Tab
                  label="Help Videos"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '15px',
                    '&.Mui-selected': {
                      color: 'white',
                    },
                  }}
                />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                padding: { xs: 2, sm: 3 },
              }}
            >
              {/* Bulletin Board Tab */}
              {activeTab === 0 && (
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      color: 'rgba(67, 56, 202, 0.9)',
                      fontWeight: 700,
                      fontSize: '20px',
                    }}
                  >
                    Bulletin Board
                  </Typography>
                  {bulletinContent.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 8,
                        color: 'rgba(100, 116, 139, 0.7)',
                      }}
                    >
                      <Typography variant="body1">
                        No bulletins available at the moment.
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, fontSize: '13px' }}>
                        Check back later for updates.
                      </Typography>
                    </Box>
                  ) : (
                    <Grid2 container spacing={2}>
                      {bulletinContent.map((item, idx) => (
                        <Grid2 size={{ xs: 12 }} key={idx}>
                          <Card
                            elevation={0}
                            sx={{
                              border: '1px solid rgba(67, 56, 202, 0.15)',
                              borderRadius: 2,
                              overflow: 'hidden',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                boxShadow: '0 4px 12px rgba(67, 56, 202, 0.15)',
                                transform: 'translateY(-2px)',
                              },
                            }}
                          >
                            <CardContent>
                              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                                {item.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {item.content}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid2>
                      ))}
                    </Grid2>
                  )}
                </Box>
              )}

              {/* Help Videos Tab */}
              {activeTab === 1 && (
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      color: 'rgba(67, 56, 202, 0.9)',
                      fontWeight: 700,
                      fontSize: '20px',
                    }}
                  >
                    Help Videos
                  </Typography>
                  {playlistLinks?.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 8,
                        color: 'rgba(100, 116, 139, 0.7)',
                      }}
                    >
                      <Typography variant="body1">No videos available.</Typography>
                    </Box>
                  ) : (
                    <Grid2 container spacing={2}>
                      {playlistLinks?.map((link, idx) => {
                        if (link?.type === 'playlist' && link?.playlistVideoIds) {
                          return link.playlistVideoIds.map((video, videoIdx) => (
                            <Grid2 size={{ xs: 12, sm: 6 }} key={`${idx}-${videoIdx}`}>
                              <VideoThumbnailCard
                                video={video}
                                onClick={() => {
                                  setSelectedVideo(video)
                                  setModalOpen(true)
                                }}
                              />
                            </Grid2>
                          ))
                        } else if (link?.contentId) {
                          return (
                            <Grid2 size={{ xs: 12, sm: 6 }} key={idx}>
                              <VideoThumbnailCard
                                video={link}
                                onClick={() => {
                                  setSelectedVideo(link)
                                  setModalOpen(true)
                                }}
                              />
                            </Grid2>
                          )
                        }
                        return null
                      })}
                    </Grid2>
                  )}
                </Box>
              )}
            </Box>
          </Drawer>
          )}

      {/* Fullscreen Video Modal with Smooth Transition */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fullScreen
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.95)',
            backdropFilter: 'blur(20px)',
            m: 0,
            maxWidth: '100%',
            maxHeight: '100%',
          },
        }}
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 300,
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)',
          },
        }}
      >
        <Box
          position="relative"
          width="100%"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            padding: { xs: 1, sm: 2 },
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: '1600px',
              position: 'relative',
              borderRadius: { xs: 0, sm: 2 },
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              aspectRatio: '16/9',
              maxHeight: '90vh',
            }}
          >
            {selectedVideo && (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                }}
              >
                <iframe
                  src={`https://www.youtube.com/embed/${selectedVideo.contentId}?autoplay=1&rel=0`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={selectedVideo.name || 'Help Video'}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  }}
                />
              </Box>
            )}
          </Box>
          <IconButton
            onClick={() => {
              setModalOpen(false)
              setSelectedVideo(null)
            }}
            sx={{
              position: 'absolute',
              top: { xs: 8, sm: 16 },
              right: { xs: 8, sm: 16 },
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              color: 'rgba(0, 0, 0, 0.87)',
              '&:hover': {
                bgcolor: 'white',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease',
              zIndex: 1300,
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedVideo?.name && (
            <Box
              sx={{
                position: 'absolute',
                bottom: { xs: 8, sm: 16 },
                left: { xs: 8, sm: 16 },
                right: { xs: 8, sm: 16 },
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                padding: 2,
                zIndex: 1300,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  fontSize: { xs: '16px', sm: '20px' },
                }}
              >
                {selectedVideo.name}
              </Typography>
            </Box>
          )}
        </Box>
      </Dialog>
    </>
  )
}
